import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useTenantId } from './useTenantId';
import { toast } from '@/hooks/use-toast';

export interface ContactCommunication {
  id: string;
  user_id: string;
  contact_id: string;
  communication_type: 'email' | 'call' | 'sms' | 'meeting' | 'note';
  direction: 'inbound' | 'outbound' | 'bidirectional';
  subject?: string;
  message?: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'sent' | 'delivered' | 'read' | 'failed';
  scheduled_at?: string;
  completed_at?: string;
  duration_minutes?: number;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CreateCommunicationInput {
  contact_id: string;
  communication_type: ContactCommunication['communication_type'];
  direction: ContactCommunication['direction'];
  subject?: string;
  message?: string;
  status?: ContactCommunication['status'];
  scheduled_at?: Date;
  completed_at?: Date;
  duration_minutes?: number;
  metadata?: Record<string, any>;
}

export function useContactCommunications(contactId: string | null) {
  const { user } = useAuth();
  const { tenantId } = useTenantId();
  const queryClient = useQueryClient();

  // Fetch communications for a contact
  const { data: communications = [], isLoading } = useQuery({
    queryKey: ['contact-communications', contactId],
    queryFn: async () => {
      if (!contactId || !user) return [];

      const { data, error } = await supabase
        .from('contact_communications')
        .select('*')
        .eq('contact_id', contactId)
        .order('completed_at', { ascending: false, nullsFirst: false })
        .order('scheduled_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ContactCommunication[];
    },
    enabled: !!contactId && !!user,
  });

  // Create new communication
  const createCommunication = useMutation({
    mutationFn: async (input: CreateCommunicationInput) => {
      if (!user) throw new Error('User not authenticated');
      if (!tenantId) throw new Error('Tenant not found');

      const { data, error } = await supabase
        .from('contact_communications')
        .insert([{
          user_id: user.id,
          tenant_id: tenantId,
          contact_id: input.contact_id,
          communication_type: input.communication_type,
          direction: input.direction,
          subject: input.subject,
          message: input.message,
          status: input.status || 'completed',
          scheduled_at: input.scheduled_at?.toISOString(),
          completed_at: input.completed_at?.toISOString(),
          duration_minutes: input.duration_minutes,
          metadata: input.metadata || {},
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-communications', contactId] });
      toast({
        title: 'Success',
        description: 'Communication logged successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to log communication',
        variant: 'destructive',
      });
    },
  });

  // Update communication
  const updateCommunication = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ContactCommunication> }) => {
      const { data, error } = await supabase
        .from('contact_communications')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user?.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-communications', contactId] });
      toast({
        title: 'Success',
        description: 'Communication updated successfully',
      });
    },
  });

  // Delete communication
  const deleteCommunication = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('contact_communications')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-communications', contactId] });
      toast({
        title: 'Success',
        description: 'Communication deleted successfully',
      });
    },
  });

  return {
    communications,
    isLoading,
    createCommunication: createCommunication.mutateAsync,
    updateCommunication: updateCommunication.mutateAsync,
    deleteCommunication: deleteCommunication.mutateAsync,
  };
}
